import { useId, useState } from "react";
import { useNavigate } from "react-router";

import { ApiError } from "@/api/client";
import { queryKeys } from "@/api/hooks";
import {
  useAssetVersionQuery,
  useCreateLibraryAssetMutation,
  useLibraryPartManifestQuery,
  type LibraryAsset,
} from "@/api/library";
import { useRetryPausedQuery } from "@/api/useRetryPausedQuery";
import {
  isPartsContent,
  toDraft,
  toPartsContent,
  type DraftPart,
} from "@/lib/assetContent";
import {
  UNWRITTEN_PROMPT_TEXT,
  manifestFor,
  requiredRoles,
} from "@/lib/namedParts";
import { createFieldErrors } from "@/ui/library/NewAssetDialog";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";
import { FieldError, joinDescriptionIds } from "@/ui/FieldError";
import { Field, Input } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import { LoadingState, UnavailableState } from "@/ui/states";

import {
  NamedReadFailureState,
  type NamedReadFailure,
} from "./NamedReadFailure";

/**
 * Create a main concern — one purpose, canonical copy (#492).
 *
 * The generic `NewAssetDialog` carries a Type picker across nine asset types and
 * a parts editor with a free-text "Part key" input. Neither may appear on this
 * family: the type is decided by the control that opened this dialog, and a part
 * key input is the manual-key escape hatch the 2026-08-25 amendment forbids. So
 * this dialog asks for a NAME, and the documents it creates are named by the
 * service's own manifest rather than by the clinician.
 *
 * ONE FIELD DIVERGES FROM THE CANONICAL DIALOG, and only one:
 *
 *   * **Pack placement.** `placementCoordinate` is required for `main_concern`
 *     and the contract states it is "explicit, never derived from the name, and
 *     immutable once stored". Deriving it would contradict that; omitting it
 *     would make every creation fail with a refusal pointing at a field nothing
 *     asked the clinician to fill in. It is the single divergence decision 2
 *     authorized, and it is not among the controls the 2026-08-25 amendment
 *     forbids.
 *
 * NO OPENING-PROMPT FIELD. An earlier arrangement here asked for the prompt text
 * during creation, because `PromptPart.text` has `minLength: 1` and a v1 with an
 * empty prompt is refused. That solved the service constraint by breaking the
 * journey this ticket is FOR: #492's accepted criterion 1 is that creating a
 * main concern asks for a name and its documents are authored afterwards, and a
 * third required field made the dialog's own "after creating it…" sentence false.
 * The required documents are therefore created with authoritative initial
 * content instead — a prompt opens on `UNWRITTEN_PROMPT_TEXT`, which says what it
 * is, and a structured document opens on `{}` — and the clinician writes the real
 * text in the document's own editor, which is the post-create step the canonical
 * journey describes.
 */

const NAME_MAX_LENGTH = 60;

/**
 * What a refusal computed in the BROWSER says, so nothing is ever swallowed.
 *
 * This dialog builds the whole parts array and hands it to `toPartsContent` before
 * publishing, and that check can refuse for reasons no field on screen owns.
 * A silent `return` there is what made "Create file" a dead control one surface
 * over, so every path out of this submit leaves a sentence behind too.
 */
const REFUSED_CONTENT =
  "This main concern's documents could not be prepared, so nothing was created.";

/**
 * What this dialog is waiting on, or why it cannot proceed.
 *
 * `unknown-type` is deliberately not a failure: it is a SUCCESSFUL manifest read
 * that names no `main_concern` entry, so the service answered and there is
 * nothing to retry.
 */
type Prerequisite =
  | { state: "pending" }
  | ({ state: "failed" } & NamedReadFailure)
  | { state: "unknown-type" }
  | { state: "ready" };

/**
 * One query's read state as a prerequisite, or `null` when it has succeeded.
 *
 * `null` rather than a `"ready"` member so the two call sites can decide what
 * success MEANS for them: a copy source that answered is ready, while a manifest
 * that answered still has to name an entry for this type.
 */
function readState(
  query: {
    isPaused: boolean;
    isPending: boolean;
    isError: boolean;
    error: unknown;
    failureReason: unknown;
  },
  retry: () => void,
): Prerequisite | null {
  if (query.isPaused) {
    return {
      state: "failed",
      failure: query.failureReason,
      isPaused: true,
      retry,
    };
  }
  if (query.isError) {
    return { state: "failed", failure: query.error, isPaused: false, retry };
  }
  if (query.isPending) {
    return { state: "pending" };
  }
  return null;
}

export function NewMainConcernDialog({
  onClose,
  copyFrom,
}: {
  onClose: () => void;
  /** Opens as "create an editable copy of this seeded asset". */
  copyFrom?: LibraryAsset;
}) {
  const navigate = useNavigate();
  const create = useCreateLibraryAssetMutation();
  const manifest = useLibraryPartManifestQuery();
  const nameErrorId = useId();
  const placementErrorId = useId();

  const [name, setName] = useState(copyFrom ? `${copyFrom.name} (copy)` : "");
  const [placementCoordinate, setPlacementCoordinate] = useState("");
  const [blocked, setBlocked] = useState<string | null>(null);

  // A copy carries the source's own documents, read at its head. Blank on the
  // placement deliberately: the source's coordinate is unique among the active
  // assets of its type, so pre-filling it offers a value guaranteed to be
  // refused (#366).
  const source = useAssetVersionQuery(
    copyFrom?.id ?? "",
    copyFrom ? copyFrom.latestVersion : null,
  );

  // Both recoveries are cancel-then-refetch. A bare `refetch()` is what the
  // paused arm above would otherwise offer, and it fires nothing in exactly the
  // state that offers it.
  const retrySource = useRetryPausedQuery(
    queryKeys.assetVersion(copyFrom?.id ?? "", copyFrom?.latestVersion ?? ""),
  );
  const retryManifest = useRetryPausedQuery(queryKeys.libraryPartManifest);

  const entry = manifestFor(manifest.data, "main_concern");
  const required = requiredRoles(entry);

  const fieldErrors = create.isError ? createFieldErrors(create.error) : {};
  const generalMessage =
    blocked ??
    (create.isError && Object.keys(fieldErrors).length === 0
      ? create.error instanceof ApiError
        ? create.error.message
        : "The Editor Service could not be reached, so nothing was created."
      : null);

  const busy = create.isPending;
  // WHICH prerequisite this dialog is waiting on, and whether it is still
  // WAITING at all. An earlier arrangement tested only `!source.isSuccess` and
  // `entry === null`, so a refused or unreachable read stayed indistinguishable
  // from a slow one: the dialog showed a skeleton for as long as it was open,
  // disabled Create, and offered nothing to retry.
  //
  // TESTING `isError` WAS STILL NOT ENOUGH, and the state it missed is the one
  // that produces the same permanent skeleton. A retry held by
  // `focusManager`/`onlineManager` keeps `status: "pending"` with
  // `isError: false`, so a paused manifest — a 503 the Editor Service really
  // sent, parked because the tab went to the background — landed back in the
  // `pending` arm and stayed there. `readState` therefore asks `isPaused`
  // FIRST, and the failing arm carries the failure itself so a 403 or a 501
  // reaches its own state rather than a Try again that cannot run.
  // A MANIFEST ENTRY THAT NAMES NO REQUIRED DOCUMENT IS THE SAME ANSWER AS NO
  // ENTRY, and it has to be derived here rather than discovered inside
  // `submit`. `requiredRoles` reads the FIRST required family, so an entry
  // whose families are empty yields no parts at all — and a v1 with no parts is
  // refused by `toPartsContent` before any request. Testing only `entry === null`
  // left Create enabled in that state, and the submit below returned in silence:
  // the same dead control the set-file dialog carried, on the roster's other
  // shape. The existing unavailable state already says exactly this, so the
  // clinician gets a reason instead of a button that does nothing.
  const prerequisite: Prerequisite = copyFrom
    ? (readState(source, retrySource) ?? { state: "ready" })
    : (readState(manifest, retryManifest) ??
      (entry === null || required.length === 0
        ? { state: "unknown-type" }
        : { state: "ready" }));

  function submit() {
    setBlocked(null);

    let parts: DraftPart[];
    if (copyFrom) {
      const stored = source.isSuccess ? source.data.content : null;
      if (stored === null || !isPartsContent(stored)) {
        // Its own sentence rather than the generic refusal below: "at least one
        // part" would describe an empty draft, when what actually happened is
        // that the source came back in a shape this build does not model. The
        // arrangement this replaces silently substituted `[]` here, which made
        // exactly that wrong refusal — and then swallowed it.
        setBlocked(
          `The Editor Service returned ${copyFrom.name} in a form this editor does not recognise, so nothing was created.`,
        );
        return;
      }
      parts = toDraft(stored);
    } else {
      parts = required.map((definition, index) => ({
        id: `new-${index}`,
        key: definition.role,
        kind: definition.kind as DraftPart["kind"],
        // The service requires these documents at v1 and refuses an empty
        // prompt, so they are created carrying content that states it is
        // unwritten rather than content the clinician was made to invent at
        // creation time.
        text: definition.kind === "structured" ? "{}" : UNWRITTEN_PROMPT_TEXT,
      }));
    }

    const content = toPartsContent(parts);
    if (!content.ok) {
      // NEVER A SILENT RETURN. The clinician pressed Create and is owed an
      // answer, whatever the remaining refusal is — a part budget, a byte
      // ceiling, a copied source this build cannot re-send. Returning here with
      // nothing said is what made "Create file" a dead control on the BG-set
      // half, and this is the same shape one dialog over.
      setBlocked(content.problems[0]?.message ?? REFUSED_CONTENT);
      return;
    }
    create.mutate(
      {
        assetType: "main_concern",
        name: name.trim(),
        content: content.content,
        note: copyFrom ? `Copied from "${copyFrom.name}" at ${copyFrom.latestVersion}.` : null,
        placementCoordinate: placementCoordinate.trim(),
      },
      {
        onSuccess(created) {
          onClose();
          void navigate(`/library/chief-complaint/${created.id}`);
        },
      },
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) {
          onClose();
        }
      }}
    >
      <DialogContent
        title={copyFrom ? `Create editable copy` : "New main concern"}
        description={
          copyFrom
            ? "Creates your own main concern starting from this one. The original stays exactly as it is."
            : "An opening template that shapes how a session begins. Its documents start unwritten — open the chief complaint afterwards to write the opening prompt."
        }
        busy={busy}
        footer={
          <>
            <Button variant="outline" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              disabled={
                busy ||
                name.trim() === "" ||
                placementCoordinate.trim() === "" ||
                prerequisite.state !== "ready"
              }
              onClick={submit}
            >
              {copyFrom ? "Create copy" : "Create main concern"}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field
            label="Name"
            description="Short name for the opening template. Its documents are authored afterwards, from the main concern's own page."
          >
            {({ inputId, describedBy }) => (
              <>
                <Input
                  id={inputId}
                  value={name}
                  maxLength={NAME_MAX_LENGTH}
                  placeholder="e.g. General intake, Military mental health intake, Trauma-focused intake"
                  aria-invalid={fieldErrors.name !== undefined || undefined}
                  aria-describedby={joinDescriptionIds(
                    describedBy,
                    fieldErrors.name ? nameErrorId : undefined,
                  )}
                  onChange={(event) => setName(event.target.value)}
                />
                {fieldErrors.name ? (
                  <FieldError id={nameErrorId} message={fieldErrors.name} />
                ) : null}
              </>
            )}
          </Field>

          <Field
            label="Pack placement"
            description="The folder this main concern's files take in a pack, as one slug — for example 'panic'. It cannot be changed after the asset exists."
          >
            {({ inputId, describedBy }) => (
              <>
                <Input
                  id={inputId}
                  value={placementCoordinate}
                  aria-invalid={
                    fieldErrors.placementCoordinate !== undefined || undefined
                  }
                  aria-describedby={joinDescriptionIds(
                    describedBy,
                    fieldErrors.placementCoordinate
                      ? placementErrorId
                      : undefined,
                  )}
                  onChange={(event) =>
                    setPlacementCoordinate(event.target.value)
                  }
                />
                {fieldErrors.placementCoordinate ? (
                  <FieldError
                    id={placementErrorId}
                    message={fieldErrors.placementCoordinate}
                  />
                ) : null}
              </>
            )}
          </Field>

          {prerequisite.state === "pending" ? (
            <LoadingState
              label={
                copyFrom
                  ? `Loading content from ${copyFrom.name}`
                  : "Loading the documents this type requires"
              }
              rows={2}
            />
          ) : null}

          {prerequisite.state === "failed" ? (
            <NamedReadFailureState
              failure={prerequisite.failure}
              isPaused={prerequisite.isPaused}
              retry={prerequisite.retry}
              subject={copyFrom ? "Asset content" : "Part manifests"}
              title={
                copyFrom
                  ? "This asset's content could not be read"
                  : "The documents this type requires could not be read"
              }
              description={
                copyFrom
                  ? `The Editor Service did not return ${copyFrom.name} at ${copyFrom.latestVersion}, so there is nothing to copy from yet.`
                  : "The Editor Service did not return which documents a main concern must carry, and creating one without them would be refused."
              }
              deniedDescription={
                copyFrom
                  ? "You are not authorized to read this asset's content, so it cannot be copied."
                  : "You are not authorized to read which documents a main concern must carry."
              }
              offlineDescription={
                copyFrom
                  ? `${copyFrom.name} at ${copyFrom.latestVersion} could not be reached, so there is nothing to copy from yet. It loads on its own when the connection returns.`
                  : "Which documents a main concern must carry could not be read, and creating one without them would be refused. It loads on its own when the connection returns."
              }
            />
          ) : null}

          {prerequisite.state === "unknown-type" ? (
            // A SUCCESSFUL manifest read that names no `main_concern` entry is
            // not a failure to retry — the service answered, and its answer is
            // that this type has no declared documents.
            <UnavailableState
              title="This type cannot be created here yet"
              description="The Editor Service does not report which documents a main concern must carry, so this editor cannot create one that it would accept."
            />
          ) : null}

          {generalMessage ? (
            <Notice tone="destructive" role="alert">
              {generalMessage}
            </Notice>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
